import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { prefetchRoute } from '@/app/routes/registry';
import {
  AppBar, Box, Divider, Drawer, IconButton, InputAdornment, ListItemIcon,
  Menu, MenuItem, Stack, TextField, Toolbar, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  Dashboard, Article, ArticleOutlined, Category, LocalOffer, Image as ImageIcon, Group, Settings,
  Menu as MenuIcon, Logout, Search,
  AccountCircle, ExpandMore, ExpandLess, MenuOpen, Insights as InsightsIcon,
  Work as WorkIcon, WorkOutline, MoveToInbox,
  AdminPanelSettings, History, GroupsOutlined, ShieldOutlined,
  HubOutlined, MoveToInboxOutlined, EmailOutlined,
} from '@mui/icons-material';
import { useAuthStore } from '@/store/auth.store';
import { useUiStore } from '@/store/ui.store';
import { useLogout } from '@/features/auth/queries';
import { usePermission } from '@/shared/hooks/usePermission';
import { useQueryClient } from '@tanstack/react-query';
import logoNhfb from '@/assets/logo-nhfb.png';

const drawerWidth = 248;
const drawerCollapsed = 76;

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
  /** Legacy shim — kept for items not yet migrated to a permission code. */
  adminOnly?: boolean;
  /** Phase-A onwards: hide the item unless the user holds this permission. */
  requires?: string;
}

/**
 * Sidebar section model — either a single item (rendered flat at the
 * top level) or a labelled collapsible group of items.
 *
 * Groups hide entirely when the user has zero permissions for any sub-item.
 * Each group remembers its open/closed state by key.
 */
type SidebarSection =
  | ({ kind: 'item' } & NavItem)
  | {
      kind: 'group';
      key: string;
      label: string;
      icon: React.ReactNode;
      items: NavItem[];
      /**
       * Optional module-dashboard path. When set, clicking the group
       * header navigates there (and keeps the sub-items expanded);
       * the chevron is still a separate hit-target for collapse only.
       * Omit on groups that don't yet have a dashboard.
       */
      path?: string;
    };

const sidebarSections: SidebarSection[] = [
  { kind: 'item', to: '/', label: 'Dashboard', icon: <Dashboard fontSize="small" />, end: true },

  // CMS — content management. All sub-areas now nested under /cms
  // (Phase 2 restructure). Posts/categories/tags are the editorial core;
  // popups + team page + SEO publish to the public site; media holds
  // reusable image assets; insights tracks readership analytics.
  {
    kind: 'group', key: 'cms', label: 'CMS', path: '/cms',
    icon: <ArticleOutlined fontSize="small" />,
    items: [
      { to: '/cms/posts',      label: 'Posts',      icon: <Article fontSize="small" /> },
      { to: '/cms/categories', label: 'Categories', icon: <Category fontSize="small" /> },
      { to: '/cms/tags',       label: 'Tags',       icon: <LocalOffer fontSize="small" /> },
      { to: '/cms/media',      label: 'Media',      icon: <ImageIcon fontSize="small" /> },
      // Popups + SEO temporarily hidden — not actively in use. The
      // routes + admin pages still work via direct URL navigation; just
      // de-cluttering the sidebar until the team wants to manage them.
      // { to: '/cms/popups',  label: 'Popups',     icon: <Campaign fontSize="small" />,   requires: 'cms.popups.read' },
      { to: '/cms/team',       label: 'Team page',  icon: <Group fontSize="small" />,      requires: 'cms.team.read' },
      // { to: '/cms/seo',     label: 'SEO',        icon: <Public fontSize="small" />,     requires: 'cms.seo.manage' },
      { to: '/cms/insights',   label: 'Insights',   icon: <InsightsIcon fontSize="small" /> },
    ],
  },

  // Careers / Jobs portal
  {
    kind: 'group', key: 'careers', label: 'Careers', path: '/careers',
    icon: <WorkOutline fontSize="small" />,
    items: [
      { to: '/careers/jobs',         label: 'Jobs',         icon: <WorkIcon fontSize="small" />,    requires: 'careers.jobs.read' },
      { to: '/careers/applications', label: 'Applications', icon: <MoveToInbox fontSize="small" />, requires: 'careers.applications.read' },
    ],
  },

  // CRM — website lead capture + advisor pipeline.
  // Leads inbox covers all form submissions (FormType=Lead/Contact/Quote/Other);
  // Newsletter is its own view because the funnel + filter set differs.
  // TODO post-deploy: switch to requires: 'crm.leads.read' once JWTs carry it.
  {
    kind: 'group', key: 'crm', label: 'CRM', path: '/crm',
    icon: <HubOutlined fontSize="small" />,
    items: [
      { to: '/crm/leads',      label: 'Leads inbox', icon: <MoveToInboxOutlined fontSize="small" />, adminOnly: true },
      { to: '/crm/newsletter', label: 'Newsletter',  icon: <EmailOutlined fontSize="small" />,       adminOnly: true },
    ],
  },

  // Administration — collapsible at the bottom. Hides entirely when
  // the user has no identity.* permissions. All sub-areas now nested
  // under /administration (Phase 2 restructure).
  {
    kind: 'group', key: 'admin', label: 'Administration', path: '/administration',
    icon: <ShieldOutlined fontSize="small" />,
    items: [
      { to: '/administration/users',          label: 'Users',          icon: <Group fontSize="small" />,              requires: 'identity.users.read' },
      // TODO post-deploy: switch /teams + /access-control back to permission-based
      // gating once JWTs carry the new identity.teams.manage / identity.access.manage
      // claims (after API ships the new permissions + everyone re-logs in).
      // For now we use the legacy adminOnly shim so Admins can preview them locally.
      { to: '/administration/teams',          label: 'Teams',          icon: <GroupsOutlined fontSize="small" />,     adminOnly: true },
      { to: '/administration/roles',          label: 'Roles',          icon: <AdminPanelSettings fontSize="small" />, requires: 'identity.roles.manage' },
      { to: '/administration/access-control', label: 'Access control', icon: <ShieldOutlined fontSize="small" />,     adminOnly: true },
      { to: '/administration/audit',          label: 'Audit log',      icon: <History fontSize="small" />,            requires: 'identity.audit.read' },
      { to: '/administration/settings',       label: 'Settings',       icon: <Settings fontSize="small" />,           requires: 'identity.settings.manage' },
    ],
  },
];

export function AppShell() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);
  const { isAdmin, has } = usePermission();
  // Permission-based gating. An item hidden when the user lacks its
  // `requires` claim; legacy `adminOnly` still shims for items that
  // haven't migrated to a permission code yet.
  const passes = (item: NavItem) =>
    (!item.adminOnly || isAdmin) &&
    (!item.requires || has(item.requires));

  // Filter sidebar sections: drop hidden flat items, drop groups whose
  // sub-items all failed the permission check.
  const visibleSections = sidebarSections
    .map((s) => {
      if (s.kind === 'item') return passes(s) ? s : null;
      const items = s.items.filter(passes);
      return items.length > 0 ? { ...s, items } : null;
    })
    .filter((s): s is SidebarSection => s !== null);

  // Per-group open/closed state. Default: all groups open.
  // Persists in localStorage so the user's choice survives reloads.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('nav.openGroups');
      if (raw) return JSON.parse(raw);
    } catch { /* fall through */ }
    return { cms: true, careers: true, admin: true };
  });
  const toggleGroup = (key: string) => {
    setOpenGroups((g) => {
      const next = { ...g, [key]: !(g[key] ?? true) };
      try { localStorage.setItem('nav.openGroups', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  // Direct "ensure open" helper. Idempotent — calling it on an already
  // open group is a no-op. Used by the group-label click handler so
  // clicking the label ALWAYS opens the submenu, never toggles it.
  const openGroup = (key: string) => {
    setOpenGroups((g) => {
      if (g[key] === true) return g; // already open, skip the rerender
      const next = { ...g, [key]: true };
      try { localStorage.setItem('nav.openGroups', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const logout = useLogout();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const location = useLocation();

  const sidebarW = collapsed && !isMobile ? drawerCollapsed : drawerWidth;

  const drawerContent = (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      bgcolor: '#FFFFFF', overflow: 'hidden',
    }}>
      {/* ── BRAND ROW WITH TOGGLE AT TOP ── */}
      {collapsed && !isMobile ? (
        // Collapsed: render a brand-styled "NH" monogram badge instead of
        // trying to crop the full wordmark. The wordmark just doesn't fit
        // a 48×48 tile cleanly across browsers; a hand-set monogram
        // guarantees a sharp, on-brand silhouette at any zoom level.
        <Box sx={{ pt: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Box
            component={NavLink}
            to="/"
            sx={{
              width: 48, height: 48,
              borderRadius: 1.75,
              background: 'linear-gradient(135deg, #DC8D0E 0%, #A66904 100%)',
              boxShadow:
                '0 4px 12px rgba(220,141,14,0.30), 0 1px 0 rgba(255,255,255,0.20) inset',
              textDecoration: 'none',
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              fontFamily: 'Poppins, system-ui, sans-serif',
              color: '#FFFAF0',
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: -0.3,
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow:
                  '0 6px 16px rgba(220,141,14,0.36), 0 1px 0 rgba(255,255,255,0.22) inset',
              },
            }}
            aria-label="NHFB home"
          >
            NH
          </Box>
          <Tooltip
            title="Expand sidebar"
            placement="right"
            arrow
            enterDelay={400}
            disableInteractive
          >
            <IconButton
              size="small"
              onClick={toggleSidebar}
              sx={{
                bgcolor: '#F1F5F9',
                width: 32, height: 32,
                '&:hover': { bgcolor: '#E2E8F0' },
              }}
            >
              <MenuOpen sx={{
                fontSize: 18, color: '#475569',
                transform: 'scaleX(-1)',
              }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        // Expanded: logo on left + toggle on right
        <Box sx={{
          px: 2,
          py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 1, minHeight: 72,
        }}>
          <Box
            component={NavLink}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', minWidth: 0, flex: 1 }}
          >
            <Box
              component="img"
              src={logoNhfb}
              alt="New Heights Financial Broker"
              sx={{
                height: 38,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
          {!isMobile && (
            <Tooltip
              title="Collapse sidebar"
              placement="right"
              arrow
              enterDelay={400}
              disableInteractive
            >
              <IconButton
                size="small"
                onClick={toggleSidebar}
                sx={{
                  bgcolor: '#F1F5F9',
                  width: 32, height: 32, flexShrink: 0,
                  '&:hover': { bgcolor: '#E2E8F0' },
                }}
              >
                <MenuOpen sx={{ fontSize: 18, color: '#475569' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <Divider sx={{ borderColor: 'rgba(15,23,42,0.06)' }} />

      {/* ── NAV ── */}
      <Box sx={{
        flex: 1,
        px: collapsed && !isMobile ? 1 : 1.5,
        pt: 1.5,
        overflowY: 'auto',
      }}>
        {/* Unified sidebar renderer — walks the section list once.
            Top-level items + collapsible groups share the same NavItem renderer. */}
        {visibleSections.map((section, sIdx) => {
          if (section.kind === 'item') {
            return (
              <SidebarNavItem
                key={section.to}
                item={section}
                collapsed={collapsed}
                isMobile={isMobile}
                onClick={() => isMobile && setMobileOpen(false)}
              />
            );
          }
          const isOpen = openGroups[section.key] ?? true;
          // Visual hairline above each group except the very first
          const showRule = sIdx > 0;
          return (
            <Box key={section.key} sx={{
              mt: showRule ? 1.2 : 0,
              pt: showRule ? 1.2 : 0,
              borderTop: showRule ? '1px solid rgba(15,23,42,0.06)' : 'none',
            }}>
              <Tooltip
                title={collapsed && !isMobile ? section.label : ''}
                placement="right"
                arrow
                enterDelay={400}
                enterNextDelay={400}
                leaveDelay={0}
                disableInteractive
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#0F172A', color: '#fff', fontSize: 12, fontWeight: 600,
                      px: 1.2, py: 0.6, borderRadius: 1,
                      boxShadow: '0 4px 14px rgba(15,23,42,0.18)',
                      '& .MuiTooltip-arrow': { color: '#0F172A' },
                    },
                  },
                }}
              >
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    // Clicking the label: navigate to the module dashboard
                    // AND open its submenu. Uses the dedicated openGroup()
                    // helper so the submenu can NEVER accidentally collapse
                    // from a label click — the chevron is the only thing
                    // that closes an open group.
                    if (section.path) {
                      navigate(section.path);
                      openGroup(section.key);
                      if (isMobile) setMobileOpen(false);
                    } else {
                      openGroup(section.key);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (section.path) {
                        navigate(section.path);
                        openGroup(section.key);
                        if (isMobile) setMobileOpen(false);
                      } else {
                        openGroup(section.key);
                      }
                    }
                  }}
                  onMouseEnter={() => section.path && prefetchRoute(section.path)}
                  onFocus={() => section.path && prefetchRoute(section.path)}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    gap: collapsed && !isMobile ? 0 : 1.25,
                    justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                    px: collapsed && !isMobile ? 1 : 1.5,
                    py: 1, mb: 0.4,
                    borderRadius: 1.25,
                    color: '#0F172A',
                    fontWeight: 700, fontSize: 11,
                    letterSpacing: 1.2, textTransform: 'uppercase',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'background-color 160ms ease',
                    // ONE hover surface — the whole header lights up
                    // together, no split chunks.
                    '&:hover': { bgcolor: 'rgba(220,141,14,0.06)' },
                    '&:focus-visible': {
                      outline: '2px solid #DC8D0E',
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', color: '#A66904' }}>
                    {section.icon}
                  </Box>
                  {(!collapsed || isMobile) && (
                    <Box sx={{ flex: 1, color: '#94A3B8', fontSize: 10.5, letterSpacing: 1.4 }}>
                      {section.label}
                    </Box>
                  )}

                  {/* Chevron lives INSIDE the same row so the hover bg
                      is continuous. stopPropagation prevents the parent
                      navigate handler from firing when the chevron is
                      clicked. Only shown when sidebar is expanded. */}
                  {(!collapsed || isMobile) && (
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={isOpen ? 'Collapse section' : 'Expand section'}
                      onClick={(e) => { e.stopPropagation(); toggleGroup(section.key); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleGroup(section.key);
                        }
                      }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center',
                        cursor: 'pointer', borderRadius: 0.8,
                        width: 22, height: 22, justifyContent: 'center',
                        outline: 'none',
                        ml: 0.4,
                        // Subtle hover only on the chevron square — no
                        // background fight with the parent hover.
                        '&:hover': { bgcolor: 'rgba(15,23,42,0.06)' },
                        '&:focus-visible': {
                          outline: '2px solid #DC8D0E',
                          outlineOffset: 0,
                        },
                      }}
                    >
                      {isOpen
                        ? <ExpandLess sx={{ fontSize: 16, color: '#94A3B8' }} />
                        : <ExpandMore sx={{ fontSize: 16, color: '#94A3B8' }} />}
                    </Box>
                  )}
                </Box>
              </Tooltip>

              {/* Strict open-state respect — collapsed sidebar no longer
                  force-shows items. If a group is closed and then the
                  sidebar collapses, the group stays closed (this was the
                  "CMS closed but sub-items appeared after collapse" bug). */}
              {isOpen && section.items.map((item) => (
                <SidebarNavItem
                  key={item.to}
                  item={item}
                  collapsed={collapsed}
                  isMobile={isMobile}
                  onClick={() => isMobile && setMobileOpen(false)}
                />
              ))}
            </Box>
          );
        })}
      </Box>

      {/* ── LOGOUT AT BOTTOM ── */}
      <Box sx={{
        px: collapsed && !isMobile ? 1 : 1.5,
        pb: 1.5, pt: 1,
        borderTop: '1px solid rgba(15,23,42,0.06)',
      }}>
        <Tooltip
          title={collapsed && !isMobile ? 'Sign out' : ''}
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#0F172A', color: '#fff',
                fontSize: 12, fontWeight: 600,
                px: 1.2, py: 0.6,
                borderRadius: 1,
                boxShadow: '0 4px 14px rgba(15,23,42,0.18)',
                '& .MuiTooltip-arrow': { color: '#0F172A' },
              },
            },
          }}
        >
          <Box
            onClick={() => logout.mutate()}
            sx={{
              display: 'flex', alignItems: 'center',
              gap: collapsed && !isMobile ? 0 : 1.25,
              justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
              px: collapsed && !isMobile ? 1 : 1.5,
              py: 1.1, mt: 0.5,
              borderRadius: 1.25,
              color: '#475569',
              fontSize: 14, fontWeight: 500,
              cursor: 'pointer',
              minHeight: 40,
              transition: 'all 180ms cubic-bezier(.2,.8,.2,1)',
              '&:hover': {
                bgcolor: 'rgba(239,68,68,0.06)',
                color: '#DC2626',
                '& .logout-icon': { color: '#DC2626' },
              },
            }}
          >
            <Box className="logout-icon" sx={{
              display: 'inline-flex', alignItems: 'center',
              color: '#94A3B8',
              transition: 'color 180ms ease',
            }}>
              <Logout fontSize="small" />
            </Box>
            {(!collapsed || isMobile) && 'Sign out'}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex', minHeight: '100vh',
      bgcolor: '#F8FAFC',
    }}>
      {/* ── SIDEBAR — rendered FIRST and at higher z-index so the logo
              area stays above the AppBar that begins to the right of it ── */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: drawerWidth, borderRight: 'none' } }}
          sx={{ zIndex: theme.zIndex.modal }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          PaperProps={{
            sx: {
              width: sidebarW,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(15,23,42,0.06)',
              overflowX: 'hidden',
              bgcolor: '#FFFFFF',
              transition: 'width 220ms cubic-bezier(.2,.8,.2,1)',
              // CRITICAL: sit ABOVE the AppBar so the sidebar logo isn't
              // overlapped by the search bar at the top.
              zIndex: theme.zIndex.appBar + 2,
            },
          }}
          sx={{
            width: sidebarW,
            flexShrink: 0,
            transition: 'width 220ms cubic-bezier(.2,.8,.2,1)',
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* ── MAIN COLUMN with AppBar that starts AFTER the sidebar ── */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: theme.zIndex.appBar + 1,
            bgcolor: '#FFFFFF',
            borderBottom: '1px solid rgba(15,23,42,0.06)',
            color: '#0F172A',
            width: { xs: '100%', md: `calc(100% - ${sidebarW}px)` },
            ml:    { xs: 0,      md: `${sidebarW}px` },
            transition: 'width 220ms cubic-bezier(.2,.8,.2,1), margin-left 220ms cubic-bezier(.2,.8,.2,1)',
          }}
        >
          <Toolbar sx={{ minHeight: 64, gap: 1.25, px: { xs: 1.5, md: 3 } }}>
            {/* Mobile-only hamburger */}
            {isMobile && (
              <IconButton edge="start" onClick={() => setMobileOpen(true)}
                sx={{
                  bgcolor: '#F1F5F9', width: 40, height: 40,
                  '&:hover': { bgcolor: '#E2E8F0' },
                }}>
                <MenuIcon sx={{ fontSize: 20, color: '#475569' }} />
              </IconButton>
            )}

            {/* ── Empty spacer pushes everything to the right ── */}
            <Box sx={{ flex: 1 }} />

            {/* Search (right-aligned cluster) */}
            <Box sx={{ width: { xs: '100%', sm: 320, md: 400 } }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search anything…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchValue.trim()) {
                    navigate(`/cms/posts?search=${encodeURIComponent(searchValue.trim())}`);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: '#94A3B8' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: '#F1F5F9',
                    borderRadius: 99,
                    fontSize: 13.5,
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: 'none' },
                    '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 3px rgba(200,163,90,0.18)' },
                    transition: 'all 180ms ease',
                    height: 40,
                  },
                }}
              />
            </Box>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1, py: 0.5, ml: 0.5,
                  borderRadius: 99,
                  cursor: 'pointer',
                  transition: 'all 180ms ease',
                  '&:hover': { bgcolor: '#F1F5F9' },
                }}
              >
                <Box sx={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)',
                  border: '2px solid #fff',
                  display: 'grid', placeItems: 'center',
                  color: '#64748B',
                  boxShadow: '0 2px 4px rgba(15,23,42,0.08)',
                }}>
                  <AccountCircle sx={{ fontSize: 26, color: '#94A3B8' }} />
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 13, fontWeight: 700, color: '#0F172A', lineHeight: 1.1,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    maxWidth: 140,
                  }}>
                    {user?.displayName ?? 'Administrator'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.2 }}>
                    {user?.roles?.[0] ?? 'Admin'}
                  </Typography>
                </Box>
                <ExpandMore sx={{ fontSize: 16, color: '#94A3B8', display: { xs: 'none', sm: 'block' } }} />
              </Box>
            </Stack>

            <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5, minWidth: 220,
                    borderRadius: 2,
                    boxShadow: '0 10px 40px rgba(15,23,42,0.12)',
                    border: '1px solid rgba(15,23,42,0.06)',
                  },
                },
              }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important', py: 1.5 }}>
                <Stack spacing={0}>
                  <Typography variant="body2" fontWeight={700}>{user?.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                </Stack>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { navigate('/administration/settings'); setAnchor(null); }}>
                <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem onClick={() => logout.mutate()}>
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            flex: 1, minWidth: 0,
            p: { xs: 2, md: 3 },
            mt: '64px',
            bgcolor: '#F8FAFC',
          }}
        >
          {location.pathname !== '/' && (
            <Box sx={{ mb: 2 }}>
              <Breadcrumb pathname={location.pathname} />
            </Box>
          )}
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Shared NavLink renderer used by both flat sidebar items and
 * grouped sub-items. Same active treatment + tooltip + collapsed
 * behavior — keeps the look consistent across sections.
 */
function SidebarNavItem({
  item, collapsed, isMobile, onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  isMobile: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip
      title={collapsed && !isMobile ? item.label : ''}
      placement="right"
      arrow
      // Delay the tooltip so casual mouse-pass-throughs don't pop a
      // tooltip the user didn't ask for; disable interactivity so the
      // tooltip dies the moment the pointer leaves the trigger.
      enterDelay={400}
      enterNextDelay={400}
      leaveDelay={0}
      disableInteractive
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#0F172A', color: '#fff', fontSize: 12, fontWeight: 600,
            px: 1.2, py: 0.6, borderRadius: 1,
            boxShadow: '0 4px 14px rgba(15,23,42,0.18)',
            '& .MuiTooltip-arrow': { color: '#0F172A' },
          },
        },
      }}
    >
      <NavLink
        to={item.to} end={item.end}
        // CRITICAL: NavLink renders as `<a>` which is inline by default,
        // so the active bg + focus ring used to wrap ONLY the inline text
        // content instead of the full row. `display: block` + `width: 100%`
        // fixes that. Also reset the browser's default focus outline; we
        // render our own brand-aligned focus-visible ring on the inner Box.
        style={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'block',
          width: '100%',
          outline: 'none',
          borderRadius: 10,
        }}
        onClick={onClick}
        onMouseEnter={() => prefetchRoute(item.to)}
        onFocus={() => prefetchRoute(item.to)}
      >
        {({ isActive }) => (
          <Box sx={{
            display: 'flex', alignItems: 'center',
            gap: collapsed && !isMobile ? 0 : 1.25,
            justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
            px: collapsed && !isMobile ? 1 : 1.5,
            py: 1.1, mb: 0.4,
            borderRadius: 1.25,
            position: 'relative',
            color: isActive ? '#9C7A2B' : '#475569',
            bgcolor: isActive ? 'rgba(200,163,90,0.10)' : 'transparent',
            fontWeight: isActive ? 600 : 500,
            fontSize: 14,
            transition: 'background-color 160ms ease, color 160ms ease',
            cursor: 'pointer',
            minHeight: 40,
            '&:hover': {
              bgcolor: isActive ? 'rgba(200,163,90,0.14)' : 'rgba(15,23,42,0.04)',
            },
            // Branded focus-visible ring around the WHOLE row, not just text
            'a:focus-visible &': {
              outline: '2px solid #DC8D0E',
              outlineOffset: -2,
            },
          }}>
            {isActive && (
              <Box sx={{
                position: 'absolute',
                left: collapsed && !isMobile ? -4 : -8,
                width: 4, height: 22, borderRadius: 4,
                background: 'linear-gradient(180deg, #DC8D0E 0%, #9C7A2B 100%)',
              }} />
            )}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              color: isActive ? '#9C7A2B' : '#94A3B8',
            }}>
              {item.icon}
            </Box>
            {(!collapsed || isMobile) && item.label}
          </Box>
        )}
      </NavLink>
    </Tooltip>
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Pretty-label override for segments where `cap(seg)` would read badly
// ("Cms", "Crm", "Seo"). Add new entries when you add module routes.
const SEG_LABELS: Record<string, string> = {
  cms: 'CMS',
  crm: 'CRM',
  seo: 'SEO',
  administration: 'Administration',
  'access-control': 'Access control',
  'find-my-mortgage-wizard': 'Wizard',
};

// Path prefixes whose top-level segment is a module bucket — used to find
// the right "owning" list when resolving a UUID's display label.
const POSTS_PATH = ['cms', 'posts'];
const JOBS_PATH = ['careers', 'jobs'];

function pathStartsWith(segs: string[], prefix: string[]) {
  return prefix.every((p, i) => segs[i] === p);
}

function Breadcrumb({ pathname }: { pathname: string }) {
  const qc = useQueryClient();
  const segs = pathname.split('/').filter(Boolean);

  // Build a label + cumulative href for each segment.
  // UUID segments are non-clickable (just labels) since there's no
  // meaningful page at /cms/posts/<uuid> on its own — but every other
  // segment routes to a real listing.
  const crumbs = segs.map((s, i) => {
    const href = '/' + segs.slice(0, i + 1).join('/');
    let label: string = SEG_LABELS[s] ?? cap(s);
    let clickable = true;

    if (s === 'new') {
      label = 'New';
      clickable = false;
    } else if (s === 'edit') {
      label = 'Edit';
      clickable = false;
    } else if (UUID_RE.test(s)) {
      if (pathStartsWith(segs, POSTS_PATH)) {
        const cached = qc.getQueryData<{ title?: string }>(['posts', 'detail', s]);
        label = cached?.title ?? 'Edit post';
      } else if (pathStartsWith(segs, JOBS_PATH)) {
        const cached = qc.getQueryData<{ title?: string }>(['careers', 'jobs', 'detail', s]);
        label = cached?.title ?? 'Edit job';
      } else {
        label = 'Detail';
      }
      clickable = false;
    }

    return { label, href, clickable };
  });

  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.6,
      fontSize: 12.5, fontFamily: 'inherit',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    }}>
      {/* Always-visible Home crumb so / is reachable from any page */}
      <Box
        component={NavLink} to="/"
        sx={{
          color: '#94A3B8', textDecoration: 'none', fontWeight: 600,
          '&:hover': { color: '#9C7A2B' },
        }}
      >
        Home
      </Box>
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <Box key={`${c.href}-${i}`} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
            <Box component="span" sx={{ color: '#CBD5E1' }}>/</Box>
            {c.clickable && !isLast ? (
              <Box
                component={NavLink} to={c.href}
                sx={{
                  color: '#94A3B8', textDecoration: 'none', fontWeight: 600,
                  '&:hover': { color: '#9C7A2B' },
                }}
              >
                {c.label}
              </Box>
            ) : (
              <Box component="span" sx={{ color: isLast ? '#0F172A' : '#94A3B8', fontWeight: isLast ? 700 : 500 }}>
                {c.label}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }
